import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Heart, Flame, Star, Zap, Trophy, MessageCircle,
  ThumbsUp, PartyPopper, Sparkles, Users, TrendingUp, Crown,
} from 'lucide-react';

// ─── Mock Data ──────────────────────────────────────────────────────

interface TeamMember {
  name: string;
  avatar: string;
  status: 'on_fire' | 'rolling' | 'warming_up' | 'off';
  currentStreak: number;
  todayPoints: number;
  todayUpsells: number;
  role: string;
}

const TEAM: TeamMember[] = [
  { name: 'You', avatar: 'YO', status: 'on_fire', currentStreak: 7, todayPoints: 208, todayUpsells: 6, role: 'Server' },
  { name: 'Maria G.', avatar: 'MG', status: 'on_fire', currentStreak: 5, todayPoints: 185, todayUpsells: 5, role: 'Server' },
  { name: 'James T.', avatar: 'JT', status: 'rolling', currentStreak: 3, todayPoints: 142, todayUpsells: 4, role: 'Server' },
  { name: 'Ashley R.', avatar: 'AR', status: 'rolling', currentStreak: 2, todayPoints: 118, todayUpsells: 3, role: 'Server' },
  { name: 'Kevin L.', avatar: 'KL', status: 'warming_up', currentStreak: 0, todayPoints: 67, todayUpsells: 1, role: 'Server' },
  { name: 'Priya S.', avatar: 'PS', status: 'off', currentStreak: 0, todayPoints: 0, todayUpsells: 0, role: 'Server (off today)' },
];

interface FeedItem {
  id: number;
  type: 'upsell' | 'streak' | 'achievement' | 'shoutout' | 'milestone';
  person: string;
  avatar: string;
  message: string;
  timestamp: string;
  reactions: number;
}

const LIVE_FEED: FeedItem[] = [
  { id: 1, type: 'streak', person: 'You', avatar: 'YO', message: '7-streak! New personal record!', timestamp: '2 min ago', reactions: 4 },
  { id: 2, type: 'upsell', person: 'Maria G.', avatar: 'MG', message: 'Just landed a 4-top Wagyu upsell. Table loved it!', timestamp: '8 min ago', reactions: 3 },
  { id: 3, type: 'shoutout', person: 'Manager Mike', avatar: 'MM', message: 'Amazing energy from the lunch crew today. Keep it up team!', timestamp: '15 min ago', reactions: 6 },
  { id: 4, type: 'achievement', person: 'James T.', avatar: 'JT', message: 'Unlocked "Drink Master" — 5 drink promos in one shift!', timestamp: '22 min ago', reactions: 5 },
  { id: 5, type: 'milestone', person: 'Ashley R.', avatar: 'AR', message: 'Hit 50 lifetime upsells! Halfway to Century Club.', timestamp: '35 min ago', reactions: 7 },
  { id: 6, type: 'upsell', person: 'You', avatar: 'YO', message: 'Dubai Chocolate dessert — guest said "best suggestion ever"', timestamp: '41 min ago', reactions: 2 },
  { id: 7, type: 'shoutout', person: 'Maria G.', avatar: 'MG', message: 'Shoutout to James for covering my tables during break. Team player!', timestamp: '48 min ago', reactions: 8 },
  { id: 8, type: 'streak', person: 'Maria G.', avatar: 'MG', message: '5-streak! On fire today!', timestamp: '55 min ago', reactions: 3 },
];

const statusConfig = {
  on_fire: { label: 'On Fire', color: 'text-orange-500', bg: 'bg-orange-50', border: 'border-orange-200', dot: 'bg-orange-400', icon: Flame },
  rolling: { label: 'Rolling', color: 'text-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-400', icon: Zap },
  warming_up: { label: 'Warming Up', color: 'text-blue-500', bg: 'bg-blue-50', border: 'border-blue-200', dot: 'bg-blue-400', icon: TrendingUp },
  off: { label: 'Off Today', color: 'text-gray-400', bg: 'bg-gray-50', border: 'border-gray-200', dot: 'bg-gray-300', icon: Users },
};

const feedIcons: Record<string, { icon: React.ComponentType<{ className?: string }>; color: string }> = {
  upsell: { icon: Zap, color: 'text-emerald-500' },
  streak: { icon: Flame, color: 'text-orange-500' },
  achievement: { icon: Trophy, color: 'text-amber-500' },
  shoutout: { icon: Heart, color: 'text-pink-500' },
  milestone: { icon: Star, color: 'text-indigo-500' },
};

// ─── Component ──────────────────────────────────────────────────────

const TeamPulse: React.FC = () => {
  const navigate = useNavigate();
  const [likedItems, setLikedItems] = useState<Set<number>>(new Set());
  const [teamVibeAngle, setTeamVibeAngle] = useState(0);

  useEffect(() => {
    // Animate vibe meter on mount
    const timer = setTimeout(() => setTeamVibeAngle(94), 300);
    return () => clearTimeout(timer);
  }, []);

  const toggleLike = (id: number) => {
    setLikedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totalTeamPoints = TEAM.reduce((s, t) => s + t.todayPoints, 0);
  const activeCount = TEAM.filter(t => t.status !== 'off').length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-500 to-rose-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button onClick={() => navigate('/restaurant/mockups')} className="flex items-center gap-1.5 text-pink-100 hover:text-white text-sm mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Hub
          </button>
          <div className="flex items-center gap-3">
            <Heart className="w-8 h-8 text-pink-200" />
            <div>
              <h1 className="text-2xl font-bold text-white">Team Pulse</h1>
              <p className="text-pink-100 text-sm">Gen Korean BBQ, Fremont — Live</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Team vibe + stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Vibe meter */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 text-center">
            <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Team Vibe</div>
            <div className="relative w-24 h-24 mx-auto mb-2">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="42" fill="none" stroke="#f3f4f6" strokeWidth="8" />
                <circle
                  cx="50" cy="50" r="42" fill="none" stroke="url(#vibeGrad)" strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${(teamVibeAngle / 100) * 264} 264`}
                  className="transition-all duration-1000"
                />
                <defs>
                  <linearGradient id="vibeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#ec4899" />
                    <stop offset="100%" stopColor="#f97316" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl font-bold text-gray-900">{teamVibeAngle}%</span>
              </div>
            </div>
            <div className="text-xs text-pink-500 font-medium flex items-center justify-center gap-1">
              <Sparkles className="w-3 h-3" /> Incredible energy today!
            </div>
          </div>

          {/* Team stats */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="text-xs text-gray-500 mb-3 uppercase tracking-wider">Today's Team Stats</div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Team Points</span>
                <span className="text-sm font-bold text-gray-900">{totalTeamPoints}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Active Servers</span>
                <span className="text-sm font-bold text-gray-900">{activeCount}/{TEAM.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Upsells</span>
                <span className="text-sm font-bold text-emerald-600">{TEAM.reduce((s, t) => s + t.todayUpsells, 0)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">On-Fire Servers</span>
                <span className="text-sm font-bold text-orange-500">{TEAM.filter(t => t.status === 'on_fire').length}</span>
              </div>
            </div>
          </div>

          {/* Daily team goal */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="text-xs text-gray-500 mb-3 uppercase tracking-wider">Team Goal</div>
            <div className="text-center mb-3">
              <div className="text-sm text-gray-600 mb-1">Today's Target: 25 upsells</div>
              <div className="text-3xl font-bold text-gray-900">{TEAM.reduce((s, t) => s + t.todayUpsells, 0)}/25</div>
            </div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden mb-2">
              <div
                className="h-full bg-gradient-to-r from-pink-400 to-orange-400 rounded-full transition-all duration-1000"
                style={{ width: `${(TEAM.reduce((s, t) => s + t.todayUpsells, 0) / 25) * 100}%` }}
              />
            </div>
            <p className="text-[11px] text-gray-400 text-center">
              Hit the goal and everyone gets <span className="font-medium text-pink-500">+50 bonus points</span>
            </p>
          </div>
        </div>

        {/* Team roster */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Your Crew</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {TEAM.map((member, i) => {
              const sc = statusConfig[member.status];
              const StatusIcon = sc.icon;
              return (
                <div
                  key={i}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                    member.name === 'You' ? 'border-pink-200 bg-pink-50/50' : `${sc.border} ${sc.bg}`
                  }`}
                >
                  {/* Avatar */}
                  <div className="relative">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold ${
                      member.name === 'You'
                        ? 'bg-gradient-to-br from-pink-400 to-orange-400 text-white'
                        : member.status === 'off' ? 'bg-gray-200 text-gray-500' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {member.avatar}
                    </div>
                    {member.status !== 'off' && (
                      <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full ${sc.dot} border-2 border-white`} />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium text-gray-900 truncate">{member.name}</span>
                      {member.name === 'You' && (
                        <Crown className="w-3.5 h-3.5 text-amber-400" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <StatusIcon className={`w-3 h-3 ${sc.color}`} />
                      <span className={sc.color}>{sc.label}</span>
                      {member.currentStreak > 0 && (
                        <span className="text-orange-400">{member.currentStreak}-streak</span>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  {member.status !== 'off' && (
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-bold text-gray-900">{member.todayPoints} pts</div>
                      <div className="text-xs text-gray-400">{member.todayUpsells} upsells</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Live feed */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
            <h2 className="text-base font-semibold text-gray-900">Live Feed</h2>
          </div>

          <div className="space-y-0 divide-y divide-gray-50">
            {LIVE_FEED.map((item) => {
              const fi = feedIcons[item.type];
              const Icon = fi.icon;
              const liked = likedItems.has(item.id);
              return (
                <div key={item.id} className="flex items-start gap-3 py-3">
                  {/* Avatar */}
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    item.person === 'You'
                      ? 'bg-gradient-to-br from-pink-400 to-orange-400 text-white'
                      : item.person.includes('Manager')
                        ? 'bg-indigo-100 text-indigo-600'
                        : 'bg-gray-100 text-gray-600'
                  }`}>
                    {item.avatar}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-sm font-medium text-gray-900">{item.person}</span>
                      <Icon className={`w-3.5 h-3.5 ${fi.color}`} />
                    </div>
                    <p className="text-sm text-gray-600">{item.message}</p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <button
                        onClick={() => toggleLike(item.id)}
                        className={`flex items-center gap-1 text-xs transition-colors ${
                          liked ? 'text-pink-500' : 'text-gray-400 hover:text-pink-400'
                        }`}
                      >
                        <ThumbsUp className="w-3 h-3" />
                        {item.reactions + (liked ? 1 : 0)}
                      </button>
                      <span className="text-xs text-gray-300">{item.timestamp}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom message */}
        <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl border border-pink-200 p-5 text-center">
          <PartyPopper className="w-8 h-8 text-pink-400 mx-auto mb-2" />
          <h3 className="text-sm font-semibold text-pink-800 mb-1">Winning is a team sport</h3>
          <p className="text-xs text-pink-600 max-w-md mx-auto">
            When the team hits the daily goal together, everyone earns bonus points.
            Celebrate each other's wins. Lift each other up. That's the Bespoke way.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TeamPulse;
